import React, { useEffect, useState, useCallback } from 'react';
import { Page, Card, Layout, Text, BlockStack, Spinner, Banner, Button } from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAppBridge } from '@shopify/app-bridge-react';
import { Redirect } from '@shopify/app-bridge/actions';
import { isLocalDevelopment } from '../utils/env';

// Hook seguro para usar App Bridge
function useAppBridgeSafe() {
  try {
    return useAppBridge();
  } catch {
    return null;
  }
}

const BillingConfirm: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { shopId } = useAuth();
  const app = useAppBridgeSafe();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const redirectToDashboard = useCallback(() => {
    try {
      // Usar App Bridge Redirect se disponível (quando embedado no Shopify)
      if (app && !isLocalDevelopment()) {
        try {
          const redirect = Redirect.create(app);
          // Tentar APP primeiro (mais suave)
          try {
            redirect.dispatch(Redirect.Action.APP, '/');
            return;
          } catch (appErr) {
            // Se APP não funcionar, tentar REMOTE
            try {
              const appUrl = window.location.origin;
              redirect.dispatch(Redirect.Action.REMOTE, `${appUrl}/`);
              return;
            } catch (remoteErr) {
              // Continuar para fallback
            }
          }
        } catch (err) {
          // Continuar para fallback
        }
      }

      // Fallback: tentar redirecionar a janela principal se embedado
      if (window.self !== window.top) {
        try {
          window.top!.location.href = '/';
          return;
        } catch (e) {
          // Continuar para navigate
        }
      }

      // Último fallback: usar navigate do React Router
      navigate('/');
    } catch (err) {
      // Se tudo falhar, tentar window.location como último recurso
      try {
        window.location.href = '/';
      } catch (finalErr) {
        // Se até isso falhar, pelo menos não quebrar a página
        // Silenciosamente falhar - usuário pode usar botão manual
      }
    }
  }, [app, navigate]);

  const verifySubscription = useCallback(async () => {
    if (!shopId) {
      setLoading(false);
      setError('Shop ID not found.');
      return;
    }

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      
      // Aguardar um pouco para Shopify processar (aumentado para 3 segundos)
      await new Promise(resolve => setTimeout(resolve, 3000));

      // Tentar verificar até 5 vezes com delay (aumentado de 3 para 5)
      let attempts = 0;
      let subscriptionActive = false;
      
      while (attempts < 5 && !subscriptionActive) {
        try {
          const response = await fetch(
            `${supabaseUrl}/functions/v1/billing?shopId=${shopId}`,
            {
              headers: {
                'apikey': supabaseAnonKey,
                'Authorization': `Bearer ${supabaseAnonKey}`,
                'Content-Type': 'application/json',
              },
            }
          );

          if (response.ok) {
            const data = await response.json();
            
            // Aceitar tanto 'active' quanto 'pending' (pending significa que foi aprovado mas webhook ainda não processou)
            if (data.status === 'active' || (data.status === 'pending' && data.plan === 'paid')) {
              subscriptionActive = true;
              setSuccess(true);
              // Redirecionar automaticamente após 1 segundo se sucesso
              setTimeout(() => {
                redirectToDashboard();
              }, 1000);
              break;
            }
          }
        } catch (fetchErr) {
          // Continuar tentando mesmo com erro de rede
        }
        
        attempts++;
        if (attempts < 5 && !subscriptionActive) {
          // Aguardar mais 2 segundos antes da próxima tentativa
          await new Promise(resolve => setTimeout(resolve, 2000));
        }
      }

      if (!subscriptionActive) {
        // Se ainda não está ativo, assumir que foi aprovado mas webhook ainda não processou
        // Redirecionar mesmo assim após mostrar mensagem
        setError('Subscription is being processed. Redirecting to dashboard...');
        setTimeout(() => {
          redirectToDashboard();
        }, 2000);
      }
    } catch (err) {
      // Mesmo com erro, assumir que foi aprovado e redirecionar
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(`Unable to verify subscription status (${errorMessage}). Redirecting to dashboard...`);
      setTimeout(() => {
        redirectToDashboard();
      }, 2000);
    } finally {
      setLoading(false);
    }
  }, [shopId, redirectToDashboard]);

  useEffect(() => {
    // Preservar parâmetros do App Bridge na URL (shop, host) se estiverem presentes
    // Isso garante que o App Bridge funcione corretamente mesmo após redirecionamento
    const urlParams = new URLSearchParams(window.location.search);
    const shopParam = urlParams.get('shop');
    
    // Se temos shop na URL mas não está no localStorage, salvar
    if (shopParam && !localStorage.getItem('shop_domain')) {
      localStorage.setItem('shop_domain', shopParam);
    }
    
    // Se não tem shopId, não podemos continuar
    if (!shopId) {
      setError('Shop ID not found. Please try accessing the billing page again.');
      setLoading(false);
      return;
    }

    // Verificar status da assinatura após confirmação
    verifySubscription();

    // Timeout de segurança: redirecionar após 10 segundos mesmo se verificação falhar
    const timeoutId = setTimeout(() => {
      setLoading((prevLoading) => {
        if (prevLoading) {
          setError('Taking longer than expected. Redirecting to dashboard...');
          setTimeout(() => {
            redirectToDashboard();
          }, 2000);
          return false;
        }
        return prevLoading;
      });
    }, 10000);

    return () => clearTimeout(timeoutId);
  }, [shopId, redirectToDashboard, verifySubscription]);

  if (loading) {
    return (
      <Page>
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <BlockStack gap="300" align="center">
                  <Spinner size="large" />
                  <Text as="p" tone="subdued" alignment="center">
                    {t('billing.verifying', { defaultValue: 'Verificando assinatura...' })}
                  </Text>
                </BlockStack>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page title={t('billing.confirmTitle', { defaultValue: 'Confirming Subscription' })}>
      <Layout>
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              {success ? (
                <>
                  <Banner tone="success">
                    {t('billing.success', { 
                      defaultValue: 'Subscription activated successfully! Thank you for your support.' 
                    })}
                  </Banner>
                  <Text as="p" tone="subdued">
                    {t('billing.redirecting', { 
                      defaultValue: 'Redirecting to dashboard...' 
                    })}
                  </Text>
                  <Button variant="primary" onClick={redirectToDashboard}>
                    {t('common.goToDashboard', { defaultValue: 'Go to Dashboard' })}
                  </Button>
                </>
              ) : (
                <>
                  {error ? (
                    <Banner tone="warning">
                      {error}
                    </Banner>
                  ) : (
                    <Banner tone="info">
                      {t('billing.processing', { 
                        defaultValue: 'Processing your subscription. Please wait...' 
                      })}
                    </Banner>
                  )}
                  <BlockStack gap="200">
                    <Button variant="primary" onClick={redirectToDashboard}>
                      {t('common.goToDashboard', { defaultValue: 'Go to Dashboard' })}
                    </Button>
                    <Button onClick={() => navigate('/billing')}>
                      {t('billing.backToBilling', { defaultValue: 'Back to Billing' })}
                    </Button>
                  </BlockStack>
                </>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
};

export default BillingConfirm;

